/**
 * Usercentrics CMP settings IDs are sourced from per-locale runtime env vars
 * (NUXT_PUBLIC_USERCENTRICS_SETTINGS_ID_DE_DE, …NL_NL, …FI_FI, …EN_GB).
 * Legacy NUXT_USERCENTRICS_SETTINGS_ID_* names are accepted during migration.
 *
 * They MUST be resolved at server start (runtime), not when nuxt.config.ts is
 * imported during `pnpm build`. Reading process.env in static configs (e.g.
 * apps/{ags,mad}/config/shops.ts) bakes the value into the bundle, and in
 * Docker the build container is missing these vars — they live in Kamal
 * `env.clear` and only reach the runtime container. That mismatch caused the
 * CMP to silently not initialise on preview/preprod (script tag rendered with
 * data-settings-id="undefined", Usercentrics loader refused, `window.UC_UI`
 * never defined, `#cmp-open` clicks turned into no-ops).
 *
 * Keep these helpers pure so they can be exercised from Jest without booting
 * Nuxt. useUsercentricsSettingsIds wires the env into SSR state at runtime.
 */

const LEGACY_ENV_PREFIX = 'NUXT_USERCENTRICS_SETTINGS_ID_'
const PUBLIC_ENV_PREFIX = 'NUXT_PUBLIC_USERCENTRICS_SETTINGS_ID_'

export type UsercentricsSettingsIds = Record<string, string>

export function buildUsercentricsSettingsIds(
  env: Record<string, string | undefined>,
): UsercentricsSettingsIds {
  const result: UsercentricsSettingsIds = {}

  for (const prefix of [PUBLIC_ENV_PREFIX, LEGACY_ENV_PREFIX]) {
    for (const key of Object.keys(env)) {
      if (!key.startsWith(prefix)) continue
      const suffix = key.slice(prefix.length)
      if (!suffix) continue
      const value = env[key]
      result[suffix] = value ?? ''
    }
  }

  return result
}

export function buildUsercentricsLookupKey(shop: {
  isoLanguageCode: string
  country: string
}): string {
  // MAD's UK shop carries country='UK' for legacy reasons, but the env var
  // suffix follows the ISO 3166 alpha-2 country code (GB). Normalise here so
  // both apps can use the same lookup without duplicating logic.
  const country = shop.country === 'UK' ? 'GB' : shop.country
  return `${shop.isoLanguageCode.toUpperCase()}_${country}`
}
